import { renderRichBlockHtml } from "../editor/richText.mjs";
import { isTiptapDocument } from "../editor/articleDocument.mjs";
import { renderTiptapWechatDocument } from "./tiptapWechatRenderer.mjs";

const IMAGE_PLACEHOLDER_PATTERN = /^!\[图片占位：(.+?)\]\(\)\s*(.+)$/;

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function parseImagePlaceholderParagraph(paragraph) {
  const match = String(paragraph || "").match(IMAGE_PLACEHOLDER_PATTERN);
  if (!match) return null;

  const title = match[1].trim();
  const rest = match[2].trim();
  const colonIndex = rest.indexOf("：");
  return {
    title,
    placement: colonIndex === -1 ? rest : rest.slice(0, colonIndex).trim(),
    description: colonIndex === -1 ? "" : rest.slice(colonIndex + 1).trim()
  };
}

function renderArticleParagraphHtml(paragraph) {
  return renderRichBlockHtml(paragraph);
}

const TEMPLATE_BUILDERS = {
  fresh: buildFreshPublishHtml,
  professional: buildProfessionalPublishHtml,
  magazine: buildMagazinePublishHtml
};

export function articleToWechatPublishHtml(article, options = {}) {
  const templateId = options.templateId || "fresh";
  const builder = TEMPLATE_BUILDERS[templateId] || TEMPLATE_BUILDERS.fresh;
  return builder(article);
}

export function wrapClipboardHtml(html) {
  return `<!DOCTYPE html><html><body><!--StartFragment-->${html}<!--EndFragment--></body></html>`;
}

function buildFreshPublishHtml(article) {
  const styles = {
    root: "color:#303b46;font-size:14px;line-height:1.92;",
    h1: "margin:0 0 12px;color:#15202b;font-size:22px;font-weight:800;line-height:1.35;letter-spacing:-0.02em;",
    summary:
      "margin:0 0 4px;padding:13px 14px;border:1px solid #b9e4cf;border-left:4px solid #0a9f6a;border-radius:8px;color:#315746;background:#eaf7f0;font-size:14px;line-height:1.82;",
    section: "margin-top:26px;",
    h2: "margin:0 0 12px;color:#15202b;font-size:16px;font-weight:800;line-height:1.45;",
    h3: "margin:20px 0 10px;color:#15202b;font-size:16px;font-weight:800;line-height:1.5;",
    h4: "margin:18px 0 9px;color:#243443;font-size:15px;font-weight:800;line-height:1.55;",
    h5: "margin:16px 0 8px;color:#315746;font-size:14px;font-weight:800;line-height:1.6;",
    h2Prefix:
      "display:inline-block;width:4px;height:16px;margin-right:8px;background:#0a9f6a;vertical-align:-2px;border-radius:999px;",
    p: "margin:0 0 12px;color:#303b46;font-size:14px;line-height:1.92;",
    blockquote:
      "margin:14px 0;padding:12px 14px;border-left:3px solid #0a9f6a;border-radius:0 8px 8px 0;color:#31455f;background:#eaf7f0;",
    list: "margin:10px 0 14px;padding-left:22px;color:#303b46;",
    taskList: "margin:10px 0 14px;padding-left:0;color:#303b46;list-style:none;",
    taskMarker: "display:inline-block;width:22px;color:#0a9f6a;font-weight:800;",
    li: "margin:6px 0;line-height:1.92;",
    strong: "color:#15202b;font-weight:800;",
    em: "font-style:italic;",
    u: "text-decoration:underline;",
    s: "text-decoration:line-through;",
    mark: "padding:1px 4px;border-radius:4px;background:#fff0b8;",
    a: "color:#0a9f6a;text-decoration:underline;",
    codeInline: "padding:1px 4px;border-radius:4px;color:#245bdb;background:#f2f3f5;font-family:Menlo,Consolas,monospace;",
    code: "color:#26323d;font-family:Menlo,Consolas,monospace;",
    codeBlock: "margin:14px 0;padding:14px 16px;border-radius:8px;background:#f4f7f9;white-space:pre-wrap;overflow-wrap:anywhere;",
    hr: "height:1px;margin:22px 0;border:0;background:#dfe5e9;",
    imageWrap: "margin:16px 0;text-align:center;",
    imageFrame:
      "display:block;min-height:132px;padding:18px 16px;border:1px dashed #c5d0d8;border-radius:10px;color:#66717e;background:linear-gradient(180deg,#fbfcfd 0%,#f4f7f9 100%);",
    imageBadge:
      "display:inline-block;margin-bottom:6px;padding:0 8px;min-height:22px;line-height:22px;border:1px solid #b9e4cf;border-radius:999px;color:#0a9f6a;background:#eaf7f0;font-size:11px;font-weight:700;letter-spacing:0.04em;",
    imageTitle: "display:block;color:#15202b;font-size:14px;line-height:1.5;font-weight:700;",
    imageMeta: "display:block;margin-top:6px;color:#8a95a3;font-size:12px;line-height:1.6;"
  };

  return renderPublishSource(article, "fresh", styles);
}

function buildProfessionalPublishHtml(article) {
  const styles = {
    root: "color:#2a3440;font-size:14px;line-height:1.86;",
    hero: "padding-bottom:16px;border-bottom:1px solid #dde5ea;margin-bottom:6px;",
    h1: "margin:0 0 10px;color:#15202b;font-size:21px;font-weight:800;line-height:1.35;letter-spacing:-0.01em;",
    titleRule: "width:48px;height:3px;margin:0 0 12px;background:#2f6fde;border-radius:999px;font-size:0;line-height:0;",
    summaryLabel:
      "display:block;margin-bottom:6px;color:#2f6fde;font-size:11px;font-weight:800;letter-spacing:0.14em;",
    summary:
      "margin:0 0 4px;padding:13px 14px;border:1px solid #c8dbff;border-left:4px solid #2f6fde;border-radius:4px;color:#28486c;background:#edf4ff;font-size:14px;line-height:1.82;",
    section:
      "margin-top:20px;padding:16px 16px 12px;border:1px solid #edf1f4;border-radius:6px;background:linear-gradient(180deg,#fbfcfd 0%,#fff 100%);",
    h2: "margin:0 0 12px;color:#15202b;font-size:15px;font-weight:800;line-height:1.45;letter-spacing:0.02em;",
    h3: "margin:20px 0 10px;color:#15202b;font-size:16px;font-weight:800;line-height:1.5;",
    h4: "margin:18px 0 9px;color:#28486c;font-size:15px;font-weight:800;line-height:1.55;",
    h5: "margin:16px 0 8px;color:#456582;font-size:14px;font-weight:800;line-height:1.6;",
    h2Square:
      "display:inline-block;width:8px;height:8px;margin-right:8px;background:#2f6fde;border-radius:2px;vertical-align:1px;",
    p: "margin:0 0 12px;color:#2a3440;font-size:14px;line-height:1.86;",
    blockquote:
      "margin:14px 0;padding:12px 14px;border-left:3px solid #2f6fde;border-radius:0 4px 4px 0;color:#31455f;background:#f5f8fc;",
    list: "margin:10px 0 14px;padding-left:22px;color:#2a3440;",
    taskList: "margin:10px 0 14px;padding-left:0;color:#2a3440;list-style:none;",
    taskMarker: "display:inline-block;width:22px;color:#2f6fde;font-weight:800;",
    li: "margin:6px 0;line-height:1.86;",
    strong: "color:#15202b;font-weight:800;",
    em: "font-style:italic;",
    u: "text-decoration:underline;",
    s: "text-decoration:line-through;",
    mark: "padding:1px 4px;border-radius:4px;background:#fff0b8;",
    a: "color:#2f6fde;text-decoration:underline;",
    codeInline: "padding:1px 4px;border-radius:3px;color:#245bdb;background:#edf4ff;font-family:Menlo,Consolas,monospace;",
    code: "color:#26323d;font-family:Menlo,Consolas,monospace;",
    codeBlock: "margin:14px 0;padding:14px 16px;border:1px solid #dde5ea;border-radius:4px;background:#f5f8fc;white-space:pre-wrap;overflow-wrap:anywhere;",
    hr: "height:1px;margin:22px 0;border:0;background:#dde5ea;",
    imageWrap: "margin:16px 0;text-align:center;",
    imageFrame:
      "display:block;min-height:132px;padding:18px 16px;border:1px dashed #c5d0d8;border-radius:6px;color:#66717e;background:#f5f8fc;",
    imageBadge:
      "display:inline-block;margin-bottom:6px;padding:0 8px;min-height:22px;line-height:22px;border:1px solid #c8dbff;border-radius:999px;color:#2f6fde;background:#edf4ff;font-size:11px;font-weight:700;letter-spacing:0.04em;",
    imageTitle: "display:block;color:#15202b;font-size:14px;line-height:1.5;font-weight:700;",
    imageMeta: "display:block;margin-top:6px;color:#8a95a3;font-size:12px;line-height:1.6;"
  };

  return renderPublishSource(article, "professional", styles, { heroStyle: styles.hero, titleRule: true, summaryLabel: "摘要" });
}

function buildMagazinePublishHtml(article) {
  const styles = {
    root: "color:#3a342c;font-size:15px;line-height:1.9;",
    hero:
      "margin-bottom:18px;padding:28px 20px 18px;background:linear-gradient(180deg,#fff9ef 0%,#fff 100%);",
    h1:
      "margin:0 0 12px;color:#15202b;font-size:26px;font-weight:700;line-height:1.35;letter-spacing:0.04em;font-family:'Songti SC','Noto Serif SC',STSong,Georgia,serif;",
    summary:
      "margin:0;padding:13px 0 0;border-top:1px solid #eadfce;color:#6b5434;background:transparent;font-size:14px;line-height:1.82;font-style:italic;",
    section: "margin-top:32px;",
    h2:
      "margin:0 0 12px;padding-bottom:10px;border-bottom:1px solid #eadfce;color:#15202b;font-size:17px;font-weight:700;line-height:1.45;letter-spacing:0.06em;font-family:'Songti SC','Noto Serif SC',STSong,Georgia,serif;",
    h3: "margin:22px 0 10px;color:#15202b;font-size:17px;font-weight:700;line-height:1.5;font-family:'Songti SC','Noto Serif SC',STSong,Georgia,serif;",
    h4: "margin:20px 0 9px;color:#5c4f3d;font-size:16px;font-weight:700;line-height:1.55;font-family:'Songti SC','Noto Serif SC',STSong,Georgia,serif;",
    h5: "margin:18px 0 8px;color:#6b5434;font-size:15px;font-weight:700;line-height:1.6;font-family:'Songti SC','Noto Serif SC',STSong,Georgia,serif;",
    h2Diamond:
      "display:inline-block;width:7px;height:7px;margin-right:10px;background:#d99018;transform:rotate(45deg);vertical-align:2px;",
    p: "margin:0 0 12px;color:#3a342c;font-size:15px;line-height:1.9;",
    blockquote:
      "margin:14px 0;padding:16px 18px 16px 42px;border:none;border-radius:2px;color:#5c4f3d;background:#faf6ef;font-style:italic;position:relative;",
    quoteMark:
      "position:absolute;left:14px;top:10px;color:#d99018;font-family:Georgia,'Times New Roman',serif;font-size:30px;line-height:1;font-style:normal;",
    list: "margin:10px 0 14px;padding-left:22px;color:#3a342c;",
    taskList: "margin:10px 0 14px;padding-left:0;color:#3a342c;list-style:none;",
    taskMarker: "display:inline-block;width:22px;color:#d99018;font-weight:800;",
    li: "margin:6px 0;line-height:1.9;",
    strong: "color:#15202b;font-weight:800;",
    em: "font-style:italic;",
    u: "text-decoration:underline;",
    s: "text-decoration:line-through;",
    mark: "padding:1px 4px;border-radius:4px;background:#ffe9b8;",
    a: "color:#a86810;text-decoration:underline;",
    codeInline: "padding:1px 4px;color:#8a5a14;background:#fff7ea;font-family:Menlo,Consolas,monospace;",
    code: "color:#4b4135;font-family:Menlo,Consolas,monospace;",
    codeBlock: "margin:16px 0;padding:16px 18px;border:1px solid #eadfce;background:#faf6ef;white-space:pre-wrap;overflow-wrap:anywhere;",
    hr: "height:1px;margin:24px 0;border:0;background:#eadfce;",
    imageWrap: "margin:16px 0;text-align:center;",
    imageFrame:
      "display:block;min-height:148px;padding:18px 16px;border:1px solid #eadfce;border-radius:2px;color:#66717e;background:linear-gradient(180deg,#fffdf8 0%,#faf6ef 100%);",
    imageBadge:
      "display:inline-block;margin-bottom:6px;padding:0 8px;min-height:22px;line-height:22px;border:1px solid #f0d39a;border-radius:999px;color:#c47a12;background:#fff7ea;font-size:11px;font-weight:700;letter-spacing:0.04em;",
    imageTitle: "display:block;color:#15202b;font-size:14px;line-height:1.5;font-weight:700;",
    imageMeta: "display:block;margin-top:6px;color:#8a95a3;font-size:12px;line-height:1.6;"
  };

  return renderPublishSource(article, "magazine", styles, { heroStyle: styles.hero, magazineQuote: true });
}

function renderPublishSource(article, templateId, styles, options = {}) {
  if (isTiptapDocument(article?.contentJson)) {
    return renderTiptapWechatDocument(article.contentJson, { title: article?.title, styles });
  }
  return renderPublishDocument(article, templateId, styles, options);
}

function renderPublishDocument(article, templateId, styles, options = {}) {
  const sections = (article.sections || [])
    .map((section) => renderPublishSection(section, templateId, styles, options))
    .join("\n");

  const heroOpen = options.heroStyle
    ? `<section style="${options.heroStyle}">`
    : `<section style="margin-bottom:6px;">`;
  const titleRule = options.titleRule ? `<p style="${styles.titleRule}">&nbsp;</p>` : "";
  const summaryLabel = options.summaryLabel
    ? `<span style="${styles.summaryLabel}">${options.summaryLabel}</span>`
    : "";

  const title = String(article.title || "").trim() || "未命名文章";
  return `<section style="${styles.root}">
${heroOpen}
  <h1 style="${styles.h1}">${escapeHtml(title)}</h1>
  ${titleRule}
  <p style="${styles.summary}">${summaryLabel}${escapeHtml(article.summary)}</p>
</section>
${sections}
</section>`;
}

function renderPublishSection(section, templateId, styles, options) {
  const heading = renderPublishHeading(section.heading, templateId, styles);
  const paragraphs = (section.paragraphs || [])
    .map((paragraph) => renderPublishParagraph(paragraph, styles, options))
    .join("\n  ");

  return `<section style="${styles.section}">
  ${heading}
  ${paragraphs}
</section>`;
}

function renderPublishHeading(heading, templateId, styles) {
  const text = escapeHtml(heading);
  if (templateId === "fresh") {
    return `<h2 style="${styles.h2}"><span style="${styles.h2Prefix}"></span>${text}</h2>`;
  }
  if (templateId === "professional") {
    return `<h2 style="${styles.h2}"><span style="${styles.h2Square}"></span>${text}</h2>`;
  }
  if (templateId === "magazine") {
    return `<h2 style="${styles.h2}"><span style="${styles.h2Diamond}"></span>${text}</h2>`;
  }
  return `<h2 style="${styles.h2}">${text}</h2>`;
}

function renderPublishParagraph(paragraph, styles, options) {
  const placeholder = parseImagePlaceholderParagraph(paragraph);
  if (placeholder) {
    return renderPublishImagePlaceholder(placeholder, styles);
  }

  const html = renderArticleParagraphHtml(paragraph);
  return applyInlineStyles(html, styles, options);
}

function renderPublishImagePlaceholder(placeholder, styles) {
  const meta = `${escapeHtml(placeholder.placement)}${placeholder.description ? `：${escapeHtml(placeholder.description)}` : ""}`;
  return `<section style="${styles.imageWrap}">
  <section style="${styles.imageFrame}">
    <span style="${styles.imageBadge}">图片占位</span>
    <strong style="${styles.imageTitle}">${escapeHtml(placeholder.title)}</strong>
    <span style="${styles.imageMeta}">${meta}</span>
  </section>
</section>`;
}

function applyInlineStyles(html, styles, options = {}) {
  let value = String(html || "").trim();
  if (!value) return "";

  if (value.startsWith("<blockquote")) {
    if (options.magazineQuote) {
      value = value.replace(
        /^<blockquote\b[^>]*>/i,
        `<blockquote style="${styles.blockquote}"><span style="${styles.quoteMark}">“</span>`
      );
    } else {
      value = value.replace(/^<blockquote\b[^>]*>/i, `<blockquote style="${styles.blockquote}">`);
    }
    return inlineInlineTags(value, styles);
  }

  if (/^<(ul|ol)\b/i.test(value)) {
    value = value.replace(/^<(ul|ol)\b[^>]*>/i, (match, tag) => `<${tag} style="${styles.list}">`);
    value = value.replace(/<li\b[^>]*>/gi, `<li style="${styles.li}">`);
    return inlineInlineTags(value, styles);
  }

  value = value.replace(/^<p\b[^>]*>/i, `<p style="${styles.p}">`);
  return inlineInlineTags(value, styles);
}

function inlineInlineTags(html, styles) {
  return html
    .replace(/<strong\b[^>]*>/gi, `<strong style="${styles.strong}">`)
    .replace(/<b\b[^>]*>/gi, `<strong style="${styles.strong}">`)
    .replace(/<\/b>/gi, "</strong>")
    .replace(/<em\b[^>]*>/gi, `<em style="${styles.em}">`)
    .replace(/<i\b[^>]*>/gi, `<em style="${styles.em}">`)
    .replace(/<\/i>/gi, "</em>")
    .replace(/<u\b[^>]*>/gi, `<u style="${styles.u}">`)
    .replace(/<s\b[^>]*>/gi, `<s style="${styles.s}">`)
    .replace(/<strike\b[^>]*>/gi, `<s style="${styles.s}">`)
    .replace(/<del\b[^>]*>/gi, `<s style="${styles.s}">`)
    .replace(/<mark\b[^>]*>/gi, `<mark style="${styles.mark}">`)
    .replace(/<a\b([^>]*)>/gi, (match, attrs) => {
      const href = attrs.match(/\shref=(["'])(.*?)\1/i)?.[2] || "";
      if (!/^https?:\/\//i.test(href)) return `<span style="${styles.a}">`;
      return `<a style="${styles.a}" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">`;
    });
}
